import * as React from 'react';
import { FieldWrapper } from '@progress/kendo-react-form';
import BaseDatePicker from 'shared/inputs/datePickers/BaseDatePicker';
import { FloatingLabel, Error, Hint } from '@progress/kendo-react-labels';
// eslint-disable-next-line no-unused-vars
import BaseFormControl from './BaseFormControl';

/**
 * A date picker form field.
 *
 * @typedef {React.ComponentProps<BaseFormControl>} BaseFieldRenderProps
 * @typedef {React.ComponentPropsWithoutRef<BaseDatePicker>} BaseDatePickerProps
 *
 * @param {BaseFieldRenderProps & BaseDatePickerProps} fieldRenderProps
 * @returns {React.ReactElement} Date picker form field.
 */
const FormDatePicker = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    name,
    valid,
    disabled,
    hint,
    wrapperStyle,
    hintDirection,
    wrapperClassName,
    optional,
    value,
    ...others
  } = fieldRenderProps;

  const editorId = id ?? name;

  const showValidationMessage = touched && validationMessage;
  const showHint = !showValidationMessage && hint;
  const hintId = showHint ? `${editorId}_hint` : '';
  const errorId = showValidationMessage ? `${editorId}_error` : '';
  const labelId = label ? `${editorId}_label` : '';

  if (!label) {
    return (
      <FieldWrapper style={wrapperStyle} className={wrapperClassName}>
        <BaseDatePicker
          ariaLabelledBy={labelId}
          ariaDescribedBy={`${hintId} ${errorId}`}
          valid={valid}
          id={editorId}
          name={name}
          disabled={disabled}
          value={value}
          dataTestId={editorId}
          {...others}
        />
        {showHint && <Hint id={hintId} direction={hintDirection}>{hint}</Hint>}
        {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
      </FieldWrapper>
    );
  }

  return (
    <FieldWrapper style={wrapperStyle} className={wrapperClassName}>
      <FloatingLabel
        label={label}
        id={labelId}
        optional={optional}
        editorId={editorId}
        editorValid={valid}
        editorDisabled={disabled}
        editorValue={value}
      >
        <BaseDatePicker
          ariaLabelledBy={labelId}
          ariaDescribedBy={`${hintId} ${errorId}`}
          valid={valid}
          id={editorId}
          name={name}
          disabled={disabled}
          value={value}
          dataTestId={editorId}
          {...others}
        />

      </FloatingLabel>
      {showHint && <Hint id={hintId} direction={hintDirection}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  );
};

export default FormDatePicker;
